import { SHADE, TAU, WHITE, circle, glow, rim } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

export const CREAM = 0xfff6dc;
export const CLEATS = 0x1b2140;
export const HAIR = 0x2a1a12;

export const HANDS = { along: 2, across: 14.5 } as const;
export const SHOES = { across: 6 } as const;
export const STRIDE = 7;
export const BALL_RADIUS = 6;
const BALL_PATCH = '#1b2140';
const BALL_LEATHER = '#fff6dc';

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function torsoPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 9.5, 13.5, 0, 0, TAU);
}

function jersey(ctx: Ctx): void {
  torsoPath(ctx);
  ctx.fillStyle = '#e4e4e4';
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(1.5, 0, 4.75, 8.9, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
  torsoPath(ctx);
  rim(ctx, 1.5);
}

export const NUMBER_AT = -5;
function number(ctx: Ctx): void {
  ctx.save();
  ctx.rotate(Math.PI / 2);
  ctx.strokeStyle = WHITE;
  ctx.lineWidth = 1.25;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(-3.4, -1.3);
  ctx.lineTo(-2.1, -2.4);
  ctx.lineTo(-2.1, 2.4);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(1.7, 0, 1.45, 2.3, 0, 0, TAU);
  ctx.stroke();
  ctx.restore();
}

function shoe(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 5.2, 3, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.1);
}

function hand(ctx: Ctx): void {
  circle(ctx, 3.2);
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.lineWidth = 0.9;
  ctx.strokeStyle = SHADE;
  ctx.stroke();
}

function head(ctx: Ctx): void {
  circle(ctx, 6.4, 4, 0);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.1);
}

function hair(ctx: Ctx): void {
  circle(ctx, 5.3, 3, 0);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function band(ctx: Ctx): void {
  ctx.beginPath();
  ctx.arc(3, 0, 5.3, -0.4, 0.4);
  ctx.strokeStyle = WHITE;
  ctx.lineWidth = 1.4;
  ctx.lineCap = 'round';
  ctx.stroke();
}

function ball(ctx: Ctx): void {
  const r = BALL_RADIUS;
  circle(ctx, r);
  ctx.fillStyle = BALL_LEATHER;
  ctx.fill();
  rim(ctx, 1.1);
  ctx.fillStyle = BALL_PATCH;
  ctx.beginPath();
  for (let corner = 0; corner < 5; corner += 1) {
    const angle = (corner * TAU) / 5;
    ctx.lineTo(Math.cos(angle) * r * 0.38, Math.sin(angle) * r * 0.38);
  }
  ctx.closePath();
  ctx.fill();
  for (let patch = 0; patch < 5; patch += 1) {
    const angle = (patch * TAU) / 5 + TAU / 10;
    ctx.save();
    circle(ctx, r, 0, 0);
    ctx.clip();
    circle(ctx, r * 0.2, Math.cos(angle) * r * 0.85, Math.sin(angle) * r * 0.85);
    ctx.fill();
    ctx.restore();
  }
}

export const DOT_RADIUS = 5;
function dot(ctx: Ctx): void {
  circle(ctx, DOT_RADIUS);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

const STREAK_LENGTH = 110;
function streak(ctx: Ctx): void {
  const length = STREAK_LENGTH * UNIT;
  const half = 9 * UNIT;
  const fade = ctx.createLinearGradient(length / 2, 0, -length / 2, 0);
  fade.addColorStop(0, WHITE);
  fade.addColorStop(1, 'rgb(255 255 255 / 0)');
  ctx.fillStyle = fade;
  ctx.beginPath();
  ctx.roundRect(-length / 2, -half, length, 2 * half, half);
  ctx.fill();
}

export const BALL_PIECES = {
  ball: [32, 32, REFERENCE_RADIUS, units(ball)],
  dot: [24, 24, REFERENCE_RADIUS, units(dot)],
  ballShadow: soft(10, 10),
} satisfies Record<string, Piece>;

export const SPORTIF_PIECES = {
  jersey: [48, 64, REFERENCE_RADIUS, units(jersey)],
  number: [24, 24, REFERENCE_RADIUS, units(number)],
  shoe: [24, 16, REFERENCE_RADIUS, units(shoe)],
  hand: [16, 16, REFERENCE_RADIUS, units(hand)],
  head: [48, 40, REFERENCE_RADIUS, units(head)],
  hair: [40, 40, REFERENCE_RADIUS, units(hair)],
  band: [40, 40, REFERENCE_RADIUS, units(band)],
  streak: [STREAK_LENGTH * UNIT + 4, 40, REFERENCE_RADIUS, streak],
  shadow: soft(18, 28),
  ...BALL_PIECES,
} satisfies Record<string, Piece>;
